import { useCallback, useRef, useState } from "react";

// Update the exposed level roughly this many times per second. The analyser
// itself samples every animation frame for smoothing, but re-rendering React
// state 60x/sec for a level bar is unnecessary churn - this keeps the meter
// visibly smooth while cutting render count substantially.
const STATE_UPDATE_FPS = 20;

/**
 * Lightweight microphone level meter. Same AudioContext/AnalyserNode
 * approach as the candidate visualizer already running during the live
 * interview (Conversation-v2.js's circular bar visualizer), but exposes a
 * single smoothed 0-1 number instead of drawing to a canvas, so callers can
 * drive a simple level bar and/or silence detection.
 */
export default function useMicLevel() {
  const [level, setLevel] = useState(0);

  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);
  const peakLevelRef = useRef(0);
  const smoothedRef = useRef(0);
  const lastUpdateRef = useRef(0);

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {
        // Already disconnected - safe to ignore.
      }
      sourceRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close().catch(() => {});
    }
    audioContextRef.current = null;
    setLevel(0);
  }, []);

  const start = useCallback(
    (stream) => {
      if (!stream) return;
      stop();
      peakLevelRef.current = 0;
      smoothedRef.current = 0;
      lastUpdateRef.current = 0;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      sourceRef.current = source;

      const smoothingFactor = 0.7;
      const minUpdateIntervalMs = 1000 / STATE_UPDATE_FPS;

      const tick = (now) => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const normalized = average / 255;

        smoothedRef.current =
          smoothedRef.current * smoothingFactor + normalized * (1 - smoothingFactor);
        peakLevelRef.current = Math.max(peakLevelRef.current, normalized);

        if (now - lastUpdateRef.current >= minUpdateIntervalMs) {
          lastUpdateRef.current = now;
          setLevel(smoothedRef.current);
        }

        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [stop]
  );

  const getPeakLevel = useCallback(() => peakLevelRef.current, []);

  return { level, getPeakLevel, start, stop };
}
